import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BlockchainLogger = ({ batchData, testData, onTransactionComplete }) => {
  const [transactionStatus, setTransactionStatus] = useState('idle'); // idle, preparing, pending, confirmed, failed
  const [transactionHash, setTransactionHash] = useState('');
  const [blockNumber, setBlockNumber] = useState(null);
  const [gasUsed, setGasUsed] = useState(null);
  const [timestamp, setTimestamp] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [estimatedGas, setEstimatedGas] = useState(null);

  useEffect(() => {
    if (batchData && testData) {
      estimateTransactionCost();
    }
  }, [batchData, testData]);

  const estimateTransactionCost = async () => {
    // Simulate gas estimation
    setTimeout(() => {
      setEstimatedGas({
        gasLimit: 150000,
        gasPrice: '20 Gwei',
        estimatedCost: '0.003 ETH',
        usdCost: '$7.50'
      });
    }, 1000);
  };

  const generateMockHash = () => {
    const chars = '0123456789abcdef';
    let hash = '0x';
    for (let i = 0; i < 64; i++) {
      hash += chars?.[Math.floor(Math.random() * chars?.length)];
    }
    return hash;
  };

  const simulateBlockchainTransaction = async () => {
    setTransactionStatus('preparing');
    
    // Simulate transaction preparation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const mockHash = generateMockHash();
    setTransactionHash(mockHash);
    setTransactionStatus('pending');
    
    // Simulate network confirmation time
    await new Promise(resolve => setTimeout(resolve, 8000));
    
    // Simulate occasional failures for realism
    if (Math.random() < 0.1 && retryCount < 2) {
      setTransactionStatus('failed');
      setRetryCount(prev => prev + 1);
      return;
    }
    
    // Success
    setTransactionStatus('confirmed');
    setBlockNumber(18547892 + Math.floor(Math.random() * 1000));
    setGasUsed(142847);
    setTimestamp(new Date());
    
    if (onTransactionComplete) {
      onTransactionComplete({
        hash: mockHash,
        blockNumber: 18547892 + Math.floor(Math.random() * 1000),
        status: 'confirmed'
      });
    }
  };

  const handleRetry = () => {
    setTransactionStatus('idle');
    setTransactionHash('');
    simulateBlockchainTransaction();
  };

  const getStatusConfig = () => {
    switch (transactionStatus) {
      case 'idle':
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          icon: 'Circle',
          label: 'Ready to Log',
          description: 'Transaction ready for blockchain submission'
        };
      case 'preparing':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Clock',
          label: 'Preparing',
          description: 'Preparing transaction data for blockchain'
        };
      case 'pending':
        return {
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          icon: 'Loader2',
          label: 'Pending',
          description: 'Transaction submitted, awaiting confirmation'
        };
      case 'confirmed':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Confirmed',
          description: 'Transaction successfully recorded on blockchain'
        };
      case 'failed':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'AlertCircle',
          label: 'Failed',
          description: 'Transaction failed, retry available'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          icon: 'Circle',
          label: 'Unknown',
          description: 'Status unknown'
        };
    }
  };

  const statusConfig = getStatusConfig();

  const formatTimestamp = (date) => {
    if (!date) return '';
    return date?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const canInitiateTransaction = batchData && testData && transactionStatus === 'idle';

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Blocks" size={16} className="text-secondary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Blockchain Logger</h3>
            <p className="text-sm text-muted-foreground">
              Immutable storage of batch testing records
            </p>
          </div>
        </div>
      </div>
      {/* Transaction Status */}
      <div className={`p-4 rounded-lg ${statusConfig?.bgColor} mb-6`}>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Icon 
              name={statusConfig?.icon} 
              size={20} 
              className={`${statusConfig?.color} ${transactionStatus === 'pending' ? 'animate-spin' : ''}`}
            />
            {transactionStatus === 'confirmed' && (
              <div className="absolute inset-0 text-success animate-ping opacity-75">
                <Icon name="CheckCircle" size={20} />
              </div>
            )}
          </div>
          <div>
            <h4 className={`font-medium ${statusConfig?.color}`}>{statusConfig?.label}</h4>
            <p className="text-sm text-muted-foreground">{statusConfig?.description}</p>
          </div>
        </div>
      </div>
      {/* Transaction Details */}
      {transactionHash && (
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Transaction Hash
              </label>
              <div className="bg-muted rounded-md p-3">
                <p className="text-xs font-mono text-foreground break-all">
                  {transactionHash}
                </p>
              </div>
            </div>

            {blockNumber && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Block Number
                </label>
                <div className="bg-muted rounded-md p-3">
                  <p className="text-sm font-mono text-foreground">
                    #{blockNumber?.toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>

          {gasUsed && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Gas Used
                </label>
                <div className="bg-muted rounded-md p-3">
                  <p className="text-sm font-mono text-foreground">
                    {gasUsed?.toLocaleString()} gas
                  </p>
                </div>
              </div>

              {timestamp && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Confirmed At
                  </label>
                  <div className="bg-muted rounded-md p-3">
                    <p className="text-sm font-mono text-foreground">
                      {formatTimestamp(timestamp)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Gas Estimation */}
      {estimatedGas && transactionStatus === 'idle' && (
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <h5 className="font-medium text-foreground mb-3">Transaction Cost Estimate</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Gas Limit</p>
              <p className="font-mono text-foreground">{estimatedGas?.gasLimit?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Gas Price</p>
              <p className="font-mono text-foreground">{estimatedGas?.gasPrice}</p>
            </div>
            <div>
              <p className="text-muted-foreground">ETH Cost</p>
              <p className="font-mono text-foreground">{estimatedGas?.estimatedCost}</p>
            </div>
            <div>
              <p className="text-muted-foreground">USD Cost</p>
              <p className="font-mono text-foreground">{estimatedGas?.usdCost}</p>
            </div>
          </div>
        </div>
      )}
      {/* Data Summary */}
      {(batchData || testData) && (
        <div className="border-t border-border pt-6 mb-6">
          <h5 className="font-medium text-foreground mb-3">Data to be Logged</h5>
          <div className="space-y-3">
            {batchData && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Batch Information</span>
                <span className="text-foreground">
                  {batchData?.batchNumber} - {batchData?.productName}
                </span>
              </div>
            )}
            {testData && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Test Results</span>
                <span className="text-foreground">
                  {testData?.selectedTests?.length || 0} test{testData?.selectedTests?.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {!canInitiateTransaction && 'Complete batch and test data to enable logging'}
        </div>

        <div className="flex items-center space-x-3">
          {transactionStatus === 'failed' && (
            <Button
              variant="outline"
              onClick={handleRetry}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Retry ({retryCount}/2)
            </Button>
          )}
          
          {transactionStatus === 'confirmed' && (
            <Button
              variant="outline"
              iconName="ExternalLink"
              iconPosition="right"
            >
              View on Explorer
            </Button>
          )}

          <Button
            variant="default"
            onClick={simulateBlockchainTransaction}
            disabled={!canInitiateTransaction || ['preparing', 'pending']?.includes(transactionStatus)}
            loading={['preparing', 'pending']?.includes(transactionStatus)}
            iconName="Upload"
            iconPosition="left"
          >
            {transactionStatus === 'idle' ? 'Log to Blockchain' : 'Logging...'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlockchainLogger;